import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { ExplorationArea } from '@typings/worldmap/explorationArea.interface';
import {
  calculateRemainingTime,
  formatRemainingTime,
} from '@store/firebase/dispatch/dispatch';
import Avatar from '@components/dataDisplay/Avatar';
import DispatchDetailModal from '@pages/WorldMap/Panel/DispatchDetailModal';
import { scrollbarHide } from '@styles/variants/scrollbar';

// ─── DispatchRow ───────────────────────────────────────────────────────────────

export const DispatchRow = React.memo(function DispatchRow({
  character,
  onClick,
  delay,
}: {
  character: IPhotoCard;
  onClick: () => void;
  delay: number;
}) {
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState(false);

  const { data: areaData } = useDocument<ExplorationArea>(
    character.dispatchAreaId
      ? `${CollectionPath.WORLDMAP}/${character.dispatchAreaId}`
      : null,
  );

  useEffect(() => {
    if (!character.dispatchStartedAt || !character.dispatchDuration) return;

    const updateTimer = () => {
      const remaining = calculateRemainingTime(
        character.dispatchStartedAt!,
        character.dispatchDuration!,
      );
      if (remaining <= 0) {
        setRemainingTime('완료!');
        setIsCompleted(true);
      } else {
        setRemainingTime(formatRemainingTime(remaining));
        setIsCompleted(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [character.dispatchStartedAt, character.dispatchDuration]);

  const areaName = areaData?.name || '파견 중...';

  return (
    <DispatchItem
      isCompleted={isCompleted}
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Avatar photoURL={character.thumbnail || ''} size="32px" />
      <DispatchCharInfo>
        <DispatchCharName>{character.title}</DispatchCharName>
        <DispatchAreaName>📍 {areaName}</DispatchAreaName>
      </DispatchCharInfo>
      <DispatchTimer isCompleted={isCompleted}>
        {isCompleted ? '🎁 수령하기' : `⏱️ ${remainingTime}`}
      </DispatchTimer>
    </DispatchItem>
  );
});

// ─── DispatchListPanel ─────────────────────────────────────────────────────────

interface DispatchListPanelProps {
  /** true면 섹션 헤더(카운트 포함)를 표시 */
  showHeader?: boolean;
  /** true면 파견 캐릭터가 0명일 때 null 반환 (MiniQuest 용) */
  hideWhenEmpty?: boolean;
}

function DispatchListPanel({
  showHeader = true,
  hideWhenEmpty = false,
}: DispatchListPanelProps) {
  const currentUserId = useRecoilValue(currentUserIDState);
  const [selectedChar, setSelectedChar] = useState<IPhotoCard | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: dispatchedCharacters, mutate } = useCollection<IPhotoCard>(
    currentUserId ? CollectionPath.PHOTOCARD : null,
    {
      where: [
        ['owner', '==', currentUserId || ''],
        ['isDispatched', '==', true],
      ],
      listen: true,
    },
  );

  const handleCharClick = useCallback((character: IPhotoCard) => {
    setSelectedChar(character);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedChar(null);
  }, []);

  const handleComplete = useCallback(() => {
    mutate();
  }, [mutate]);

  if (!currentUserId) return null;

  const characters = dispatchedCharacters || [];

  if (hideWhenEmpty && characters.length === 0) return null;

  return (
    <>
      {showHeader && (
        <PanelHeader>
          🚀 파견 중 ({characters.length}명)
        </PanelHeader>
      )}
      <DispatchList>
        {characters.length > 0 ? (
          characters.map((character, index) => (
            <DispatchRow
              key={character.id}
              character={character}
              onClick={() => handleCharClick(character)}
              delay={index * 0.05}
            />
          ))
        ) : (
          <EmptyState>파견 중인 캐릭터가 없습니다.</EmptyState>
        )}
      </DispatchList>

      <DispatchDetailModal
        isOpen={isModalOpen}
        character={selectedChar}
        userId={currentUserId}
        onClose={handleModalClose}
        onComplete={handleComplete}
      />
    </>
  );
}

export default DispatchListPanel;

// ─── Styled ───────────────────────────────────────────────────────────────────

const PanelHeader = styled.div`
  color: var(--subTextColor);
  font-size: 12px;
  opacity: 0.9;
  padding: 6px 8px;
`;

const DispatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${scrollbarHide}
`;

const EmptyState = styled.div`
  padding: 20px 16px;
  text-align: center;
  color: var(--subTextColor);
  font-size: 13px;
  opacity: 0.7;
`;

const DispatchItem = styled(motion.div)<{ isCompleted: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: linear-gradient(90deg, var(--cardBackgroundColor), rgba(0, 0, 0, 0));
  border-left: 4px solid
    ${({ isCompleted }) => (isCompleted ? 'var(--primaryColor)' : 'var(--lineColor)')};
  border-radius: var(--cardBorderRadius);
  cursor: var(--hoverCursorURL), pointer;
  overflow: hidden;
  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(
      90deg,
      var(--cardBackgroundColor),
      var(--cardBackgroundColor),
      rgba(0, 0, 0, 0)
    );
  }
`;

const DispatchCharInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DispatchCharName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--textColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DispatchAreaName = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
`;

const DispatchTimer = styled.div<{ isCompleted: boolean }>`
  font-size: 12px;
  font-weight: ${({ isCompleted }) => (isCompleted ? '600' : '500')};
  color: ${({ isCompleted }) =>
    isCompleted ? 'var(--primaryColor)' : 'var(--subTextColor)'};
  white-space: nowrap;
`;
